import { memo, useMemo } from "react";
import { Text, View, Page, Document, PDFViewer } from '@react-pdf/renderer';
import { Box, Button } from "@mui/material";
import styles from "../pdf/Styles.js";

const OwnerPopulationDetailsPdf = ({
    data,
    title,
    setStep,
}) => {
    // Determine PDF type based on title - SIMPLE AND EXPLICIT
    const isLambData = title && (title.includes("αμνο") || title.includes("Αμνο"));
    
    // Determine which columns to show based on title (only for non-lamb data)
    const showLastLambingDate = title === "Στέρφα";
    const showAgeInMonths = title === "Θηλυκά Ζώα με Ηλικία > 12 μήνες" || title === "Θηλυκά Ζώα με Ηλικία > 14 μήνες";
    const showLambingDate = title === "Προβατίνες σε γαλακτοπαραγωγή";
    const showMilkingPeriod = title === "Προβατίνες σε γαλακτοπαραγωγή";

    // Sort data if title matches and data has milkingPeriod field
    const sortedData = useMemo(() => {
        if (title === "Προβατίνες σε γαλακτοπαραγωγή" && data && data.length > 0 && data[0]?.milkingPeriod) {
            // Create a copy of the data array to avoid mutating the original
            return [...data].sort((a, b) => {
                // Sort in descending order by milkingPeriod
                return b.milkingPeriod - a.milkingPeriod;
            });
        }
        // Otherwise, return the original data
        return data;
    }, [data, title]);

    const renderAnimalData = () => {
        if (!sortedData || sortedData.length === 0) {
            return (
                <View style={styles.sisterCategory}>
                    <Text style={styles.sisterCell}>Δεν υπάρχουν διαθέσιμα δεδομένα</Text>
                </View>
            );
        }

        // Render lamb data with different columns
        if (isLambData) {
            return (
                <View style={styles.sisterCategory}>
                    <View style={styles.sisterTable}>
                        <View style={styles.sisterTableHeader}>
                            <Text style={styles.sisterCell}>Α/Α</Text>
                            <Text style={styles.sisterCell}>Κωδικός Αμνού</Text>
                            <Text style={styles.sisterCell}>Φύλο</Text>
                            <Text style={styles.sisterCell}>Γεν. Κωδικός Μητέρας</Text>
                            <Text style={styles.sisterCell}>Ημερομηνία Γέννησης</Text>
                            <Text style={styles.sisterCell}>Κατάσταση</Text>
                        </View>
                        {sortedData.map((record, index) => {
                            return (
                                <View key={index} style={styles.sisterRow}>
                                    <Text style={styles.sisterCell}>{index + 1}</Text>
                                    <Text style={styles.sisterCell}>{record.lambSlug || "-"}</Text>
                                    <Text style={styles.sisterCell}>{record.sex}</Text>
                                    <Text style={styles.sisterCell}>{record.motherRegistrationCode || "-"}</Text>
                                    <Text style={styles.sisterCell}>
                                        {record.birthDate ? new Date(record.birthDate).toLocaleDateString("el-GR") : "-"}
                                    </Text>
                                    <Text style={styles.sisterCell}>{record.statusGreek || "-"}</Text>
                                </View>
                            );
                        })}
                    </View>
                </View>
            );
        }

        // Render regular animal data with base columns + optional columns based on category
        return (
            <View style={styles.sisterCategory}>
                <View style={styles.sisterTable}>
                    <View style={styles.sisterTableHeader}>
                        <Text style={styles.sisterCell}>Α/Α</Text>
                        <Text style={styles.sisterCell}>Γενεαλογικός Κωδικός</Text>
                        <Text style={styles.sisterCell}>Κωδικός Εκτροφής</Text>
                        <Text style={styles.sisterCell}>Ημερομηνία Γέννησης</Text>
                        {showLastLambingDate && (
                            <Text style={styles.sisterCell}>Τελευταία Ημερομηνία Γέννας</Text>
                        )}
                        {showAgeInMonths && (
                            <Text style={styles.sisterCell}>Ηλικία (μήνες)</Text>
                        )}
                        {showLambingDate && (
                            <Text style={styles.sisterCell}>Ημερομηνία Γέννας</Text>
                        )}
                        {showMilkingPeriod && (
                            <Text style={styles.sisterCell}>Τρέχουσα Γαλακτική Περίοδος</Text>
                        )}
                    </View>
                    {sortedData.map((record, index) => {
                        return (
                            <View key={index} style={styles.sisterRow}>
                                <Text style={styles.sisterCell}>{index + 1}</Text>
                                <Text style={styles.sisterCell}>{record.registrationCode || "-"}</Text>
                                <Text style={styles.sisterCell}>{record.flockCode || "-"}</Text>
                                <Text style={styles.sisterCell}>
                                    {record.birthDate ? new Date(record.birthDate).toLocaleDateString("el-GR") : "-"}
                                </Text>
                                {showLastLambingDate && (
                                    <Text style={styles.sisterCell}>
                                        {record.lastLambingDate ? new Date(record.lastLambingDate).toLocaleDateString("el-GR") : "-"}
                                    </Text>
                                )}
                                {showAgeInMonths && (
                                    <Text style={styles.sisterCell}>{record.ageInMonths != null ? record.ageInMonths : "-"}</Text>
                                )}
                                {showLambingDate && (
                                    <Text style={styles.sisterCell}>
                                        {record.lambingDate ? new Date(record.lambingDate).toLocaleDateString("el-GR") : "-"}
                                    </Text>
                                )}
                                {showMilkingPeriod && (
                                    <Text style={styles.sisterCell}>{record.milkingPeriod != null ? record.milkingPeriod : "-"}</Text>
                                )}
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    };

    return (
        <>
            <PDFViewer style={{ width: '85vw', height: '100vh' }}>
                <Document>
                    <Page size='A4' style={styles.page} wrap>
                        <View style={styles.container}>
                            {/* Title Page */}
                            <View style={{ fontSize: 14, margin: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <Text>{title}</Text>
                            </View>
                            {/* Milk Production Data */}
                            {renderAnimalData()}
                        </View>
                    </Page>
                </Document>
            </PDFViewer>
            {/* Back Button */}
            <Box sx={{ width: "100%", marginBottom: 3, display: "flex", justifyContent: "center" }}>
                <Button variant="outlined" color="secondary" onClick={() => setStep(2)}>
                    Πίσω
                </Button>
            </Box>
        </>
    );
};

export default memo(OwnerPopulationDetailsPdf);